/** The slim Relationships strip pinned at the bottom of the logic editor. It
 *  mirrors the selected row: its work code + name + dates, then its
 *  PREDECESSORS and SUCCESSORS as editable chips (color dot + full name +
 *  relationship). Add / edit / remove run the same tie intents the table uses,
 *  so nothing about the dependency logic changes — this is a second, roomier
 *  surface for the same edits, with full names. */
import React from "react";
import { autoFontColor } from "../../../../utils/color";
import type { DepGraph } from "../../../../models";
import type { ExternalCandidate } from "../../depCore/DepChipTypeahead/DepChipTypeahead";
import type { DepEditIntents } from "../../depCore/DepTable/intents";
import ChipCell from "../../depCore/DepTable/ChipCell/ChipCell";
import { buildCandidates } from "../../depCore/candidates";
import {
  activityLabel,
  buildSuccAdjacency,
  buildZonesByCode,
  predsFor,
} from "../../depCore/graphRows";
import { fmtMonthDay } from "../../utils/dateFormatters";
import "./RelationshipsPanel.css";

interface Props {
  graph: DepGraph;
  /** The row clicked in the table/gantt. null → the empty hint. */
  selectedId: string | null;
  intents: DepEditIntents;
  /** Query-driven out-of-section search for "+ add". */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  /** Allow editing ties to outside / finished activities. */
  allowExternalEdits: boolean;
  readOnly: boolean;
}

const RelationshipsPanel: React.FC<Props> = ({
  graph,
  selectedId,
  intents,
  fetchExternals,
  allowExternalEdits,
  readOnly,
}) => {
  const succAdjacency = React.useMemo(() => buildSuccAdjacency(graph), [graph]);
  const allCandidates = React.useMemo(
    () => buildCandidates(Object.values(graph.activities), graph.link_candidates ?? []),
    [graph],
  );
  const zonesByCode = React.useMemo(
    () => buildZonesByCode(Object.values(graph.activities)),
    [graph],
  );

  const act = selectedId ? graph.activities[selectedId] : null;

  if (!act) {
    return (
      <div className="rel-panel rel-panel--empty">
        <span className="rel-panel-hint">
          Select an activity to see and edit its predecessors and successors.
        </span>
      </div>
    );
  }

  const preds = predsFor(graph, act.id);
  const succs = succAdjacency.get(act.id) ?? [];
  const code = act.work_code || act.task_code;

  const cellProps = {
    row: act,
    variant: "row" as const,
    preds,
    succs,
    readOnly,
    allowExternalEdits,
    zonesByCode,
    allCandidates,
    fetchExternals,
    intents,
  };

  return (
    <div className="rel-panel">
      <div className="rel-panel-head">
        <span className="rel-panel-label">Relationships</span>
        <span
          className={"rel-panel-code" + (act.color ? "" : " rel-panel-code--untagged")}
          style={act.color ? { background: act.color, color: autoFontColor(act.color) } : undefined}
        >
          {code}
        </span>
        <span className="rel-panel-name">{activityLabel(act)}</span>
        <span className="rel-panel-meta">{act.task_code}</span>
        <span className="rel-panel-meta">
          {fmtMonthDay(act.start)} → {fmtMonthDay(act.finish)} · {act.duration_days}d
        </span>
      </div>
      <div className="rel-panel-cols">
        <div className="rel-panel-col rel-panel-col--pred">
          <div className="rel-panel-col-title">
            Predecessors <span className="rel-panel-col-n">{preds.length}</span>
          </div>
          <ChipCell side="pred" {...cellProps} />
        </div>
        <div className="rel-panel-col rel-panel-col--succ">
          <div className="rel-panel-col-title">
            Successors <span className="rel-panel-col-n">{succs.length}</span>
          </div>
          <ChipCell side="succ" {...cellProps} />
        </div>
      </div>
    </div>
  );
};

export default RelationshipsPanel;
